import React, { useState, useEffect, useContext } from "react";
import { Container, Button, Input, Label, Row, Col, Alert } from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import styles from "../css/ambulatoryVisit.module.scss";
import { useAmbulatoryVisitService } from "../api/useAmbulatoryVisitQuery";
import addVolunteerStyle from "../../../../modules/Volunteer/MasterVolunteer/css/volunteer.module.scss";
import SectionCard from "Components/Common/SectionCard";
import CommonTableIndexWise from "Components/Common/CommonTableIndexWise";
import formStyles from "../../../../assets/scss/common/formStyle.module.scss";
import FilePreviewModal from "Components/Common/FilePreviewModal";
import { formatDate, showToastMessage } from "common/service/CommonService";
import { AMBULATORY_VISIT_PATHS } from "Routes/paths";
import FullPageLoader from "Components/Common/FullPageLoader";
import { BreadcrumbContext } from "Layouts/Header";
import TruncatedText from "Components/Common/TruncatedText";
import Commonbadge from "../../../../Components/Common/Commonbadge";
import { DROPOUT_REASONS_OPTIONS, DROPOUT_TYPE_OPTIONS, BLOCK_REASONS_OPTIONS, loadCodelistsDetailId } from "api/modules/common.option";
import DetailField from "../../../../Components/Common/DetailField";

interface AmbulatoryFile extends File {
  attachmenttype?: string | null;
  visitactivityid?: number;
}

const ViewAmbulatorySubjectDetail: React.FC = () => {
  const { visitId, subjectId } = useParams<{ visitId: string; subjectId: string }>();
  const studyid = Number(localStorage.getItem("activestudyid") || 1);
  const breadcrumbContext = useContext(BreadcrumbContext);
  const setBreadcrumbItems = breadcrumbContext?.setBreadcrumbItems;
  const navigate = useNavigate();

  useEffect(() => {
    setBreadcrumbItems?.([
      { text: "Ambulatory Visits", link: "/study/detail/ambulatory" },
      { text: "Visit Details", link: AMBULATORY_VISIT_PATHS.DETAIL.replace(":visitId", visitId || "") },
      { text: "View Subject Activity" },
    ]);
    return () => setBreadcrumbItems?.([]);
  }, [setBreadcrumbItems, visitId]);

  const [visit, setVisit] = useState<any>(null);
  const [subject, setSubject] = useState<any>(null);
  const [subjectAssignmentId, setSubjectAssignmentId] = useState<number | null>(null);

  const [previewFile, setPreviewFile] = useState<AmbulatoryFile | null>(null);
  const [previewFileUrl, setPreviewFileUrl] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [comments, setComments] = useState("");
  const [activitiesExpanded, setActivitiesExpanded] = useState(false);

  const [decisionDetails, setDecisionDetails] = useState<any>({
    status: "",
    dropouttype: "",
    aereferenceno: "",
    dropoutdate: "",
    studystage: "",
    effectivedate: "",
    dropoutreason: "",
    detailedreason: "",
    blocktype: "Temporary",
    blockstartdate: "",
    blockenddate: "",
    blockduration: "0 days",
    blockreason: "",
    detailedremarks: "",
    rescreeningeligibility: "",
  });

  useEffect(() => {
    loadCodelistsDetailId([
      "DROPOUT_TYPE_OPTIONS",
      "DROPOUT_REASONS_OPTIONS",
      "BLOCK_REASONS_OPTIONS"
    ]).then((result) => {
      const allLoaded = Object.values(result).every((flag) => flag);
      if (!allLoaded) {
        showToastMessage("error", "Some dropdown options failed to load");
      }
    });
  }, []);

  const handlePreview = (file: any) => {
     const API_BASE_URL =  localStorage.getItem("baseUrl") || process.env.REACT_APP_BASE_URL
      const baseUrl = API_BASE_URL?.replace("/api", "") || "";

    // NEW FILE (File object)
    if (file instanceof File) {
      setPreviewFile(file);
      setPreviewFileUrl(null);
      setPreviewOpen(true);
      return;
    }

    // EXISTING FILE (API object)
    const path = file?.filepath || file?.path;

    if (!path) {
      console.error("Invalid file object:", file);
      return;
    }

    const finalUrl = path.startsWith("http")
      ? path
      : `${baseUrl}${path.startsWith("/") ? path : "/" + path}`;

    setPreviewFile(null);
    setPreviewFileUrl(finalUrl);
    setPreviewOpen(true);
  };

  const {
    SelectdataAmbulatoryVisit,
    SelectAllAssignedSubjects,
    SelectdataAmbulatoryVisitActivityResult
  } = useAmbulatoryVisitService({
    studyid,
    visitid: Number(visitId),
    subjectassignmentid: subjectAssignmentId || undefined,
  });

  useEffect(() => {
    if (SelectAllAssignedSubjects.data) {
      const subjectsList = (SelectAllAssignedSubjects.data as any)?.data || [];
      const foundSub = subjectsList.find((s: any) => String(s.subjectno || s.subjectId) === String(subjectId));
      if (foundSub) {
        const assId = Number(foundSub.visitsubjectassignmentid || foundSub.id || 0);
        if (assId && assId !== subjectAssignmentId) {
          setSubjectAssignmentId(assId);
        }
      }
    }
  }, [SelectAllAssignedSubjects.data, subjectId, subjectAssignmentId]);

  useEffect(() => {
    if (SelectdataAmbulatoryVisit.data && SelectAllAssignedSubjects.data) {
      const visitRes = (SelectdataAmbulatoryVisit.data as any)?.data;
      const visitData = visitRes?.visit?.[0];
      setVisit({
        id: visitData?.visitid || visitId,
        visitNumber: visitData?.visitnumber || `Visit ${String(visitData?.visitid || visitId).padStart(2, "0")}`,
        visitName: visitData?.visitname || "--",
        scheduledDate: visitData?.scheduleddate ? new Date(visitData.scheduleddate).toLocaleDateString([], { day: '2-digit', month: 'short', year: 'numeric' }) : "30 Jun 2026",
        visitStatus: visitData?.visitstatus || "scheduled",
      });

      const subjectsList = (SelectAllAssignedSubjects.data as any)?.data || [];
      const foundSub = subjectsList.find((s: any) => String(s.subjectno || s.subjectId) === String(subjectId));
      if (foundSub) {
        if (SelectdataAmbulatoryVisitActivityResult.data) {
          const apiRes = (SelectdataAmbulatoryVisitActivityResult.data as any)?.data;
          const results = apiRes?.activityresults || [];
          const attachmentsList = apiRes?.attachments || [];
          const decStatus = apiRes?.decisionstatus || foundSub.subjectvisitstatus || foundSub.status || "";
          const decRemarks = apiRes?.decisionremarks || "";
          const decInfo = apiRes?.decisioninfo || {};

          const mappedActivities = (visitRes?.activities || []).map((act: any) => {
            const actId = act.activityid || act.id;
            const savedResult = results.find((r: any) => Number(r.visitactivityid) === Number(actId));
            const isMandatory =
              act.ismandatory === true || act.ismandatory === 1 || String(act.ismandatory).toLowerCase() === "true" ||
              act.mandatory === true || act.mandatory === 1 || String(act.mandatory).toLowerCase() === "true";

            return {
              id: actId,
              name: act.activityname || act.name,
              status: savedResult?.subjectactivityresult ? "Completed" : "Not Started",
              result: savedResult?.subjectactivityresult || null,
              performedDate: savedResult?.createddate ? new Date(savedResult.createddate).toISOString().split('T')[0] : "",
              performedTime: "",
              performedBy: savedResult?.createdby || "",
              remarks: savedResult?.remarks || "",
              ismandatory: isMandatory
            };
          });

          setSubject({
            ...foundSub,
            subjectId: foundSub.subjectno || subjectId,
            initials: foundSub.initials || foundSub.subjectname?.slice(0, 2).toUpperCase() || "SJ",
            checkinTime: foundSub.checkintime || foundSub.checkinTime || "09:00 AM",
            status: decStatus.toLowerCase(),
            activities: mappedActivities
          });

          setComments(decRemarks);

          const statusVal = decStatus.toLowerCase();
          const initStatus = ["completed", "dropped", "blocked"].includes(statusVal) ? statusVal : "";
          setDecisionDetails({
            status: initStatus,
            dropouttype: decInfo.dropouttype || "",
            aereferenceno: decInfo.aereferenceno || "",
            dropoutdate: decInfo.dropoutdate ? decInfo.dropoutdate.split("T")[0] : "",
            studystage: decInfo.studystage || "",
            effectivedate: decInfo.effectivedate ? decInfo.effectivedate.split("T")[0] : "",
            dropoutreason: decInfo.dropoutreason || "",
            detailedreason: decInfo.dropout_remarks || decInfo.detailedreason || "",
            blocktype: decInfo.blocktype || "Temporary",
            blockstartdate: decInfo.block_startdate ? decInfo.block_startdate.split("T")[0] : "",
            blockenddate: decInfo.block_enddate ? decInfo.block_enddate.split("T")[0] : "",
            blockduration: decInfo.blockduration || "0 days",
            blockreason: decInfo.blockreason || "",
            detailedremarks: decInfo.block_remarks || decInfo.detailedremarks || "",
            rescreeningeligibility: decInfo.rescreeningeligibility || "",
          });
        }
      }
    }
  }, [
    SelectdataAmbulatoryVisit.data,
    SelectAllAssignedSubjects.data,
    SelectdataAmbulatoryVisitActivityResult.data,
    subjectId,
    visitId
  ]);

  if (SelectdataAmbulatoryVisit.isLoading || SelectAllAssignedSubjects.isLoading || (subjectAssignmentId && SelectdataAmbulatoryVisitActivityResult.isLoading) || !subject || !visit) {
    return <FullPageLoader message="Loading Subject Details..." />;
  }

  const apiAttachments = (SelectdataAmbulatoryVisitActivityResult.data as any)?.data?.attachments || [];

  const activityColumns = [
    {
      key: "name",
      label: "Activity Name",
      render: (val: any, row: any) => (
        <span className="fw-bold text-dark me-2 fs-13 d-inline-flex align-items-center">
          <TruncatedText text={val || "--"} maxWidth="200px" />
          {row.ismandatory === true && <span className="text-danger ms-1">*</span>}
        </span>
      ),
    },
    {
      key: "result",
      label: "Result",
      render: (_: any, row: any) => (
        <div className="d-flex align-items-center gap-3 fs-13">
          <Label className="d-flex align-items-center gap-1 mb-0 small fw-semibold text-success fs-13">
            <Input
              type="checkbox"
              checked={row.result === "Passed"}
              disabled={true}
              style={{ borderRadius: "4px", height: "17px" }}
            />
            Passed
          </Label>
          <Label className="d-flex align-items-center gap-1 mb-0 small fw-semibold text-danger fs-13">
            <Input
              type="checkbox"
              checked={row.result === "Failed"}
              disabled={true}
              style={{ borderRadius: "4px", height: "17px" }}
            />
            Failed
          </Label>
        </div>
      ),
    },
    {
      key: "remarks",
      label: "Remarks",
      render: (val: any) => (
        <TruncatedText text={val || "--"} maxWidth="850px" />
       ),
    }
  ];

  return (
    <div className={`page-content ${formStyles.pageContainer}`}>
      <Container fluid>

        {/* Subject Header Card */}
        <SectionCard
          icon="ri-user-search-line"
          title="Subject Identification"
          subtitle="Details of the subject and active visit check-in status."
        >
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 w-100">
            <div className="d-flex align-items-center gap-3 w-100">
              <div className={`${styles.detailMetaGrid} flex-grow-1`}>
                <div className={`${styles.metaBlock} d-flex align-items-center gap-2`}>
                  <i className="ri-user-3-line fs-20 text-muted" style={{ marginRight: "4px" }} />
                  <div>
                    <div className={styles.metaLabel}>Subject No.</div>
                    <div className="d-flex align-items-center gap-2">
                      <span className="fw-bold text-dark" style={{ fontSize: "14px" }}>{subject.subjectId}</span>
                    </div>
                  </div>
                </div>

                <div className={`${styles.metaBlock} d-flex align-items-center gap-2`}>
                  <i className="ri-passport-line fs-20 text-muted" style={{ marginRight: "4px" }} />
                  <div>
                    <div className={styles.metaLabel}>Visit Number</div>
                    <div className="d-flex align-items-center gap-2">
                      <span className="fw-bold text-dark" style={{ fontSize: "14px" }}>{visit.visitNumber}</span>
                    </div>
                  </div>
                </div>

                <div className={`${styles.metaBlock} d-flex align-items-center gap-2`}>
                  <i className="ri-bookmark-3-line fs-20 text-muted" style={{ marginRight: "4px" }} />
                  <div>
                    <div className={styles.metaLabel}>Visit Name</div>
                    <div className={styles.metaValue} style={{ color: "#0f172a", fontSize: "14px", fontWeight: 700 }}>
                      <TruncatedText text={visit.visitName || "--"} maxWidth="150px" />
                    </div>
                  </div>
                </div>

                <div className={`${styles.metaBlock} d-flex align-items-center gap-2`}>
                  <i className="ri-calendar-line fs-20 text-muted" style={{ marginRight: "4px" }} />
                  <div>
                    <div className={styles.metaLabel}>Visit Date</div>
                    <div className={styles.metaValue} style={{ color: "#0f172a", fontSize: "14px", fontWeight: 700 }}>{formatDate(visit.scheduledDate)}</div>
                  </div>
                </div>

                <div className={`${styles.metaBlock} d-flex align-items-center gap-2`}>
                  <i className="ri-time-line fs-20 text-muted" style={{ marginRight: "4px" }} />
                  <div>
                    <div className={styles.metaLabel}>Check-In</div>
                    <div className={styles.metaValue} style={{ color: "#0f172a", fontSize: "14px", fontWeight: 700 }}>
                      {subject.checkintime ? subject.checkintime.split(" ")[0] : "09:00 AM"}
                    </div>
                  </div>
                </div>

                <div className={`${styles.metaBlock} d-flex align-items-center gap-2`}>
                  <i className="ri-checkbox-circle-fill fs-20 text-muted" style={{ marginRight: "4px" }} />
                  <div>
                    <div className={styles.metaLabel}>Subject Visit Status</div>
                    <Commonbadge status={subject?.subjectvisitstatus || "checked in"} style={{ width: "140px", fontSize: "12px" }} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </SectionCard>

        {/* Column Layout / Content Panels */}
        <div className="d-flex flex-column">

          {/* Card 1: Subject Activities Table */}
          <SectionCard
            icon="ri-checkbox-line"
            title="Subject Activities"
            subtitle="Mark the status of each activity performed during this visit."
            rightContent={
              subject.activities && subject.activities.length > 5 ? (
                <Button
                  color="primary"
                  size="sm"
                  outline
                  onClick={() => setActivitiesExpanded(!activitiesExpanded)}
                >
                  {activitiesExpanded ? (
                    <>
                      <i className="ri-arrow-up-s-line me-1" /> Collapse
                    </>
                  ) : (
                    <>
                      <i className="ri-arrow-down-s-line me-1" /> Expand All
                    </>
                  )}
                </Button>
              ) : undefined
            }
          >
            <CommonTableIndexWise
              columns={activityColumns}
              data={
                subject.activities
                  ? activitiesExpanded
                    ? subject.activities
                    : subject.activities.slice(0, 5)
                  : []
              }
              hideAction={true}
              paginationShow={false}
            />
          </SectionCard>

          {/* Card 2: Attachments & Verification */}
          <SectionCard
            icon="ri-attachment-2"
            title="Attachments & Verification"
            subtitle="Supporting documents and verification details."
          >
            {apiAttachments && apiAttachments.length > 0 ? (
              <div className="mt-1 d-flex flex-column gap-2">
                <p className="text-muted fs-12 text-uppercase fw-medium ls-1 mb-2 d-block">Uploaded Documents</p>

                <Row className="gap-2 px-0 w-100 mx-auto">
                  {apiAttachments.map((file: any, idx: number) => (
                    <Col
                      key={idx}
                      md={3}
                      className={`px-0 ${addVolunteerStyle.ipInventory_wrapper}`}
                    >
                      <div className={`${addVolunteerStyle.attachmentCard} h-100`}>
                        {/* FILE INFO */}
                        <div className={addVolunteerStyle.fileInfo}>
                          <div className={addVolunteerStyle.fileIcon}>
                            <i className="ri-file-pdf-line text-danger" />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div className={addVolunteerStyle.fileName}>
                              <TruncatedText text={file.filename || file.name || "Attachment"} />
                            </div>
                            <div className={addVolunteerStyle.fileMeta}>
                              <span className="text-uppercase">{file.fileextension?.replace(".", "") || "FILE"}</span>
                              {file.filesize && <span className="ms-2">• {file.filesize}</span>}
                              {file.activityname && (
                                <span className="ms-2 text-primary fw-medium d-inline-flex align-items-center">
                                  •&nbsp;
                                  <TruncatedText text={file.activityname} maxWidth="120px" />
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className={addVolunteerStyle.fileActions}>
                          <i
                            className="ri-eye-line text-primary"
                            title="Preview"
                            style={{ cursor: "pointer" }}
                            onClick={() => handlePreview(file)}
                          />
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
              </div>
            ) : (
              <div className="mt-3">
                <p className="text-muted fs-12 text-uppercase fw-medium ls-1 mb-2 d-block">Uploaded Documents</p>
                <span className="text-muted fst-italic">No attachments uploaded.</span>
              </div>
            )}
          </SectionCard>

          {/* Card 3: Overall Decision */}
          <SectionCard
            icon="ri-shield-check-line"
            title="Overall Decision"
            subtitle="Record the overall decision or action applicable for this subject for this visit."
          >
            <div className="mb-3 pb-3 border-bottom">
              <Row>
                <Col md={12}>
                  <label className="text-muted fs-12 mb-1 text-uppercase fw-600 ls-1 d-block">
                    Overall Decision Status
                  </label>
                  <div style={{ minHeight: "24px" }}>
                    <Commonbadge
                      status={subject.status || "completed"}
                      style={{ width: "130px", fontSize: "13px", padding: "6px 12px" }}
                    />
                  </div>
                </Col>
              </Row>
            </div>

            {/* Dropped (Discontinued) Details */}
            {(subject.status?.toLowerCase() === "dropped") && (() => {
              const selectedType = DROPOUT_TYPE_OPTIONS.find(
                (opt) => String(opt.value) === String(decisionDetails.dropouttype) || opt.label === decisionDetails.dropouttype
              )?.label || (decisionDetails.dropouttype ? String(decisionDetails.dropouttype) : "—");

              const selectedReason = DROPOUT_REASONS_OPTIONS.find(
                (opt) => String(opt.value) === String(decisionDetails.dropoutreason) || opt.label === decisionDetails.dropoutreason
              )?.label || (decisionDetails.dropoutreason ? String(decisionDetails.dropoutreason) : "—");

              const isAE = selectedType === "Medical / AE";

              return (
                <div className="p-3 border rounded bg-light-subtle mb-4 shadow-sm">
                  <h6 className="fw-bold text-dark mb-3 border-bottom pb-2 font-size-13">
                    <i className="ri-logout-box-r-line me-2 text-warning" />
                    Dropout Details
                  </h6>

                  {isAE && (
                    <div className="mt-2 mb-3">
                      <Alert color="danger" className="border-0 bg-light-danger text-danger d-flex flex-column gap-1 p-3">
                        <div className="fw-semibold">
                          <i className="ri-error-warning-line me-2" />
                          Medical / Adverse Event Dropout Selected
                        </div>
                      </Alert>
                    </div>
                  )}

                  <Row>
                    <DetailField label="Dropout Type" value={selectedType} columns={4} />
                    {isAE && <DetailField label="AE Reference No." value={decisionDetails.aereferenceno} columns={4} />}
                    <DetailField label="Dropout Date" value={decisionDetails.dropoutdate ? formatDate(decisionDetails.dropoutdate) : null} columns={4} />
                    <DetailField label="Study Stage" value={decisionDetails.studystage} columns={4} />
                    <DetailField label="Effective Date" value={decisionDetails.effectivedate ? formatDate(decisionDetails.effectivedate) : null} columns={4} />
                    <DetailField label="Dropout Reason" value={selectedReason} columns={4} />
                  </Row>
                  <Row className="mt-2">
                    <DetailField
                      label="Detailed Reason"
                      value={decisionDetails.detailedreason || "No detailed reason provided."}
                      columns={12}
                      expandable={true}
                      maxChars={200}
                    />
                  </Row>
                </div>
              );
            })()}

            {/* Blocked Details */}
            {(subject.status?.toLowerCase() === "blocked") && (() => {
              const isPermanent = decisionDetails.blocktype === "Permanent";

              const selectedReason = BLOCK_REASONS_OPTIONS.find(
                (opt) => String(opt.value) === String(decisionDetails.blockreason) || opt.label === decisionDetails.blockreason
              )?.label || (decisionDetails.blockreason ? String(decisionDetails.blockreason) : "—");

              return (
                <div className="p-3 border rounded bg-light-subtle mb-4 shadow-sm">
                  <h6 className="fw-bold text-dark mb-3 border-bottom pb-2 font-size-13">
                    <i className="ri-forbid-2-line me-2 text-danger" />
                    Block Subject Details
                  </h6>
                  <Row>
                    <DetailField label="Block Type" value={decisionDetails.blocktype} columns={4} />
                    <DetailField label="Block Start Date" value={decisionDetails.blockstartdate ? formatDate(decisionDetails.blockstartdate) : null} columns={4} />
                    {!isPermanent && (
                      <>
                        <DetailField label="Block End Date" value={decisionDetails.blockenddate ? formatDate(decisionDetails.blockenddate) : null} columns={4} />
                        <DetailField label="Block Duration" value={decisionDetails.blockduration} columns={4} />
                      </>
                    )}
                    <DetailField label="Block Reason" value={selectedReason} columns={4} />
                    {!isPermanent && (
                      <DetailField label="Re-screening Eligibility" value={decisionDetails.rescreeningeligibility} columns={4} />
                    )}
                  </Row>
                  <Row className="mt-2">
                    <DetailField
                      label="Detailed Remarks"
                      value={decisionDetails.detailedremarks || "No detailed remarks provided."}
                      columns={12}
                      expandable={true}
                      maxChars={200}
                    />
                  </Row>
                </div>
              );
            })()}

            {/* Comments */}
            <Row>
              <DetailField
                label="Additional Comments / Discrepancies"
                value={comments || "No comments provided."}
                columns={12}
                expandable={true}
                maxChars={200}
              />
            </Row>
          </SectionCard>

        </div>
      </Container>

      {/* Sticky Footer */}
      <div className={formStyles.stickyFooter}>
        <div className="px-3">
        <div className={formStyles.footerActions}>
          <Button
            type="button"
            color="dark"
            outline
            onClick={() => navigate(AMBULATORY_VISIT_PATHS.DETAIL.replace(":visitId", visitId || ""))}
          >
            Back
          </Button>
        </div>
        </div>
      </div>

      {/* File Preview Modal */}
      <FilePreviewModal
        file={previewFile}
        fileUrl={previewFileUrl}
        isOpen={previewOpen}
        onClose={() => setPreviewOpen(false)}
      />
    </div>
  );
};

export default ViewAmbulatorySubjectDetail;
